/**
 * Channels (Inboxes) — the named conversation containers bound to a phone line
 * or a mailbox. Drives the inbox rail's channel list and the Settings →
 * Channels management page.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingKeys } from '@/lib/queryKeys';
import { inboxesApi } from './api';
import type { CreateChannelPayload, UpdateChannelPayload } from './types';

/** Channels the current user can see, each with its unread count. */
export function useInboxes() {
  return useQuery({
    queryKey: messagingKeys.inboxes(),
    queryFn: () => inboxesApi.list(),
    staleTime: 30_000,
  });
}

/**
 * Every channel in the tenant for Settings → Channels, including personal
 * mailboxes the viewer cannot read (listed so they can be managed). Keyed under
 * `inboxes()` so channel mutations invalidate it too.
 */
export function useManagedInboxes(enabled: boolean) {
  return useQuery({
    queryKey: [...messagingKeys.inboxes(), 'manage'] as const,
    queryFn: () => inboxesApi.listManaged(),
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateChannelPayload) => inboxesApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
    },
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateChannelPayload) =>
      inboxesApi.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
    },
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inboxesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
      // Conversations for the deleted channel are gone — refresh the lists.
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}
